import { DatePicker, Form, Select, Switch } from 'antd';
import {
  MainRowColumns,
  PageFilters,
  PhoneFilters,
} from 'components/UI/Responsive/ResponsiveFilters';
import SplitDateRangePicker from 'components/UI/Responsive/SplitDateRangePicker';
import { useMyOrderIntentionsContext } from 'contexts/MyOrderIntentionsContext';
import { OrderIntentionStatusEnum } from 'enums/orderIntentionStatus.enum';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as MyOrderIntentionEditSelectors from 'store/state/myOrderIntentionEdit/myOrderIntentionEditSelectors';
import { actions as MyOrderIntentionEditActions } from 'store/state/myOrderIntentionEdit/myOrderIntentionEditSlice';
import {
  myOrderIntentionEditSelectors,
  sessionSelectors,
} from 'store/state/selectors';
import { sessionActions } from 'store/state/slices';
import HideFilters from './HideFilters';
import {
  ORDER_INTENTIONS_ADVANCED_FIELDS,
  ORDER_INTENTIONS_CLEARED_ADVANCED_FILTERS,
  buildOrderIntentionsFilterChips,
  countActiveAdvancedFilters,
} from './orderIntentionsFilterValues';
import * as S from './responsiveFilterStyles';

const { Option } = Select;

const MAIN_ROW_COLUMNS: MainRowColumns = {
  tablet: '1fr 1fr',
  table: '1fr 1fr',
  desktop: '1fr 1fr',
};

interface Props {
  layout: 'phone' | 'tablet';
  isSheetOpen: boolean;
  onCloseSheet: () => void;
  onSubmit: () => void;
}
export default function ResponsiveFilter({
  layout,
  isSheetOpen,
  onCloseSheet,
  onSubmit,
}: Props) {
  const dispatch = useDispatch();
  const { formFilter, setFilters, activeFilters } =
    useMyOrderIntentionsContext();
  const { t } = useTranslation();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [activeAdvancedCount, setActiveAdvancedCount] = useState(0);
  const [onlyPending, setOnlyPending] = useState<boolean>(true);
  const { customers } = useSelector(sessionSelectors.getRoot);
  const isPhone = layout === 'phone';

  const selectedCustomerId = useSelector(
    sessionSelectors.getSelectedCustomerId,
  );
  const selectedCustomerIdFilter = useSelector(
    sessionSelectors.getSelectedCustomerIdFilter,
  );
  const filterExport = useSelector(
    myOrderIntentionEditSelectors.getFilterExport,
  );

  const refreshForm = useSelector(MyOrderIntentionEditSelectors.getRefreshForm);

  const defaultDate = {
    startDate: moment(moment(), 'YYYY-MM-DD'),
    endDate: moment(moment(), 'YYYY-MM-DD'),
  };

  const formatCustomerName = (customerName: string) => {
    if (customerName.length <= 22) return customerName;

    return `${customerName.substring(0, 22)}...`;
  };

  const setSelectedCustomer = useCallback(
    (value) => {
      dispatch(sessionActions.setSelectedCustomerFilter(value));
    },
    [dispatch],
  );

  const refreshAdvancedCount = useCallback(
    () =>
      setActiveAdvancedCount(
        countActiveAdvancedFilters(
          formFilter.getFieldsValue(ORDER_INTENTIONS_ADVANCED_FIELDS),
        ),
      ),
    [formFilter],
  );

  const resetAdvancedFilters = useCallback(() => {
    formFilter.resetFields(ORDER_INTENTIONS_ADVANCED_FIELDS);
    setFilters((oldValue) => ({
      ...oldValue,
      ...ORDER_INTENTIONS_CLEARED_ADVANCED_FILTERS,
    }));
    setActiveAdvancedCount(0);
  }, [formFilter, setFilters]);

  const onToggleAdvanced = () => {
    if (layout !== 'tablet') resetAdvancedFilters();
    setIsAdvancedOpen((isShown) => !isShown);
  };

  const onClear = () => {
    formFilter.resetFields();
    formFilter.setFieldsValue({ date: null, 'Data.FilterBy': null });
    refreshAdvancedCount();
  };

  const onFinish = () => {
    onSubmit();
    onCloseSheet();
    if (layout === 'tablet') setIsAdvancedOpen(false);
  };

  const onRemoveStatus = () => {
    formFilter.setFieldsValue({ 'Data.Status': undefined });
    formFilter.submit();
  };

  const onRemoveAdvanced = () => {
    resetAdvancedFilters();
    formFilter.submit();
  };

  useEffect(() => {
    formFilter.setFieldsValue({
      date: [defaultDate.startDate, defaultDate.endDate],
      'Data.FilterBy': 'P',
    });
  }, []);

  useEffect(() => {
    if (refreshForm) {
      formFilter.submit();
      dispatch(MyOrderIntentionEditActions.setRefreshForm(false));
    }
  }, [dispatch, formFilter, refreshForm]);

  useEffect(() => {
    formFilter.setFieldsValue({
      'Data.CustomerId': selectedCustomerId,
    });
  }, [formFilter, selectedCustomerId]);

  const appliedCustomer = customers.find(
    (customer) => customer.id === activeFilters['Data.CustomerId'],
  );

  const mainFields = (
    <>
      <Form.Item
        label={t('myOrderIntentions.formFilter.customers')}
        name="Data.CustomerId"
      >
        {customers?.length && (
          <Select
            showSearch
            optionFilterProp="children"
            allowClear
            defaultValue={selectedCustomerId}
            value={selectedCustomerIdFilter}
            onChange={setSelectedCustomer}
          >
            <Option key="Todos os clientes" title="Todos os clientes" value={0}>
              TODOS OS CLIENTES
            </Option>
            {customers.map(({ id, accountCode, name }) => (
              <Option key={id} value={id} title={`${accountCode} - ${name}`}>
                {`${accountCode} - ${formatCustomerName(name)}`}
              </Option>
            ))}
          </Select>
        )}
      </Form.Item>

      <Form.Item
        label={t('myOrderIntentions.formFilter.saleDate')}
        name="date"
        rules={[
          {
            required: !onlyPending,
            message: 'Selecione o período.',
          },
        ]}
      >
        {isPhone ? (
          <SplitDateRangePicker
            placeholder={[
              t('myOrderIntentions.formFilter.since'),
              t('myOrderIntentions.formFilter.until'),
            ]}
          />
        ) : (
          <DatePicker.RangePicker
            defaultValue={[defaultDate.startDate, defaultDate.endDate]}
            placeholder={[
              t('myOrderIntentions.formFilter.since'),
              t('myOrderIntentions.formFilter.until'),
            ]}
            format="DD/MM/YYYY"
          />
        )}
      </Form.Item>

      <Form.Item
        label={t('myOrderIntentions.formFilter.filterDateBy')}
        name="Data.FilterBy"
        rules={[
          {
            required: !onlyPending,
            message: 'Selecione o Filtrar por.',
          },
        ]}
      >
        <Select
          placeholder={t('myOrderIntentions.formFilter.select')}
          defaultValue="P"
          allowClear
        >
          <Option key="P" value="P">
            {t('myOrderIntentions.formFilter.publishDate')}
          </Option>
          <Option key="E" value="E">
            {t('myOrderIntentions.formFilter.deliveryDate')}
          </Option>
          <Option key="F" value="F">
            {t('myOrderIntentions.formFilter.fatDate')}
          </Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={t('myOrderIntentions.formFilter.status')}
        name="Data.Status"
      >
        <Select
          allowClear
          defaultValue={OrderIntentionStatusEnum.AGUARDANDO_PENDENTE}
          disabled={onlyPending}
          placeholder="Selecione"
        >
          <Option
            key={OrderIntentionStatusEnum.CONFIRMADO}
            value={OrderIntentionStatusEnum.CONFIRMADO}
          >
            {t('myOrderIntentions.formFilter.statusOptions.generate')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.CONFIRMADO_PARCIAL}
            value={OrderIntentionStatusEnum.CONFIRMADO_PARCIAL}
          >
            {t('myOrderIntentions.formFilter.statusOptions.notGenerate')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.CONFIRMADO_ALTERACAO}
            value={OrderIntentionStatusEnum.CONFIRMADO_ALTERACAO}
          >
            {t('myOrderIntentions.formFilter.statusOptions.changed')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.PENDENTE}
            value={OrderIntentionStatusEnum.PENDENTE}
          >
            {t('myOrderIntentions.formFilter.statusOptions.open')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.AGUARDANDO_GERACAO_PEDIDO}
            value={OrderIntentionStatusEnum.AGUARDANDO_GERACAO_PEDIDO}
          >
            {t('myOrderIntentions.formFilter.statusOptions.process')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.AGUARDANDO_PRODUTOR}
            value={OrderIntentionStatusEnum.AGUARDANDO_PRODUTOR}
          >
            {t('myOrderIntentions.formFilter.statusOptions.waitingProducer')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.RECUSADO}
            value={OrderIntentionStatusEnum.RECUSADO}
          >
            {t('myOrderIntentions.formFilter.statusOptions.refused')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.CANCELADO}
            value={OrderIntentionStatusEnum.CANCELADO}
          >
            {t('myOrderIntentions.formFilter.statusOptions.canceled')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.EXPIRADO}
            value={OrderIntentionStatusEnum.EXPIRADO}
          >
            {t('myOrderIntentions.formFilter.statusOptions.programmed')}
          </Option>
          <Option
            key={OrderIntentionStatusEnum.ERRO}
            value={OrderIntentionStatusEnum.ERRO}
          >
            {t('myOrderIntentions.formFilter.statusOptions.error')}
          </Option>
          <Option
            disabled
            style={{ display: 'none' }}
            key={OrderIntentionStatusEnum.AGUARDANDO_PENDENTE}
            value={OrderIntentionStatusEnum.AGUARDANDO_PENDENTE}
          >
            {t('myOrderIntentions.formFilter.statusOptions.waitingAndOpen')}
          </Option>
        </Select>
      </Form.Item>

      <S.SwitchField>
        <Form.Item name="OnlyPending" noStyle>
          <S.SwitchComponent>
            <Switch
              aria-label="Exibir apenas pendentes"
              checked={onlyPending}
              onChange={(checked) => {
                formFilter.setFieldsValue({
                  'Data.Status': checked ? 'S' : null,
                });
                setOnlyPending(checked);
              }}
            />
          </S.SwitchComponent>
        </Form.Item>
        <span>Exibir apenas pendentes</span>
      </S.SwitchField>
    </>
  );

  const showsAdvancedFields = isAdvancedOpen || activeAdvancedCount > 0;

  const layoutProps = {
    mainFields,
    advancedFields: showsAdvancedFields ? (
      <S.StackedColumns>
        <HideFilters />
      </S.StackedColumns>
    ) : null,
    activeAdvancedCount,
    isAdvancedOpen,
    onToggleAdvanced,
    onClear,
    onSearchAndClose: () => formFilter.submit(),
  };

  return (
    <Form
      layout="vertical"
      form={formFilter}
      onFinish={onFinish}
      onValuesChange={() => window.setTimeout(refreshAdvancedCount)}
      data-testid="order-intentions-filters"
      initialValues={{
        'Data.StartDate': undefined,
        'Data.EndDate': undefined,
        'Data.Status': 'S',
        'Data.Origin': undefined,
        'Data.ProductCategoryId': undefined,
        'Data.ProductGroupId': undefined,
        'Data.ProductId': undefined,
        'Data.ProductQualityId': undefined,
        'Data.SiteId': undefined,
        'Data.NatureOperationId': undefined,
        'Data.Recipient': undefined,
        'Data.PublishedBy': undefined,
        'Data.CustomerId': undefined,
      }}
    >
      {isPhone ? (
        <PhoneFilters
          {...layoutProps}
          chips={
            filterExport
              ? buildOrderIntentionsFilterChips(activeFilters, {
                  t,
                  customerLabel: appliedCustomer
                    ? `${appliedCustomer.accountCode} - ${appliedCustomer.name}`
                    : 'Todos os clientes',
                  onRemoveStatus,
                  onRemoveAdvanced,
                })
              : []
          }
          isSheetOpen={isSheetOpen}
          onCloseSheet={onCloseSheet}
          inlineActions
        />
      ) : (
        <PageFilters
          {...layoutProps}
          layout="tablet"
          mainRowColumns={MAIN_ROW_COLUMNS}
          onSearch={() => formFilter.submit()}
        />
      )}
    </Form>
  );
}
